import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Row, Col, Table, Alert, message, Form, DatePicker } from "antd";
import { AUTH_HEADER_NAME } from "common/apis";
import { AppPageHeader } from "components/AppPageHeader";
import { AppUpload } from "components/AppUpload";
import { useAppSelector } from "hooks/useAppSelector";
import { PartnerVariant, usePartnerServicesConfig } from "hooks/usePartnerServicesConfig";
import { generateHeaders } from "common/utils";
import { ColumnsType } from "antd/lib/table";
import { useDocumentTitle } from "hooks/usePageTitle";

const UploadEarningPayoutsPage = ({ variant = "ppd" }: { variant?: PartnerVariant }) => {
  useDocumentTitle("Upload Earning Payouts");
  const { authToken } = useAppSelector((state) => state.app.user);
  const config = usePartnerServicesConfig(variant);
  const {
    dispatch,
    processingEarningPayouts,
    uploadGigPayoutsUrl,
    partnerRoutes,
    setEarningPayoutsAction,
    removeEarningPayoutsAction,
  } = config;

  const navigate = useNavigate();
  const [hasErrorRows, setHasErrorRows] = useState(false);
  const [month, setMonth] = useState<string>();

  useEffect(() => {
    const { timeUploaded } = processingEarningPayouts;
    if (timeUploaded) {
      const uploadedTime = new Date(timeUploaded).getTime();
      const currentTime = new Date().getTime();
      const minsPassed = (currentTime - uploadedTime) / 1000 / 60;
      if (minsPassed >= 15) dispatch(removeEarningPayoutsAction());
    }
    if (processingEarningPayouts?.key) {
      navigate(`/${partnerRoutes.SAVE_EARNING_PAYOUTS}`);
    }
    return () => { message.destroy(); };
  }, [processingEarningPayouts, navigate, dispatch, removeEarningPayoutsAction, partnerRoutes]);

  useEffect(() => {
    if (processingEarningPayouts?.failedRows?.length) {
      setHasErrorRows(true);
    }
  }, [processingEarningPayouts?.failedRows]);

  const headers: { [name: string]: string } = {
    ...(authToken && { [AUTH_HEADER_NAME]: authToken }),
  };

  const onUploaded = (data: any) => dispatch(setEarningPayoutsAction(data));
  const onUploading = () => dispatch(removeEarningPayoutsAction());

  const { headerMapping } = processingEarningPayouts;
  const headerMappingWithError = headerMapping
    ? headerMapping + ",payableEarnings:Payable Earnings,alreadyPaidAmount:Already Paid Amount,errors:Errors"
    : "";

  const generateMemoizedHeaders = useMemo(
    () => generateHeaders(headerMappingWithError),
    [headerMappingWithError]
  );

  const tableHeaders = generateMemoizedHeaders || {};
  const successRows = processingEarningPayouts?.successRows || [];
  const failedRows = processingEarningPayouts?.failedRows || [];
  const successData = successRows.map((row, i) => ({ ...row, key: `data-success-${i}` }));
  const failedData = failedRows.map((row, i) => ({ ...row, key: `data-failed-${i}` }));
  const data = [...failedData, ...successData];

  const columns: ColumnsType<any & { key: string }> = [
    ...Object.keys(tableHeaders).map((key) => ({
      key,
      title: tableHeaders[key],
      dataIndex: key,
      render: (text: string, record: any) => {
        if (key === "errors") {
          return <>{record.errors?.map((e: any) => e.message).join(", ")}</>;
        }
        return <>{text}</>;
      },
    })),
  ];

  const addBorderToErrorRows = (record: any) =>
    record.errors?.length ? "error-row" : "";

  return (
    <>
      <Row>
        <Col>
          <AppPageHeader title="Upload Earning Payouts" />
        </Col>
      </Row>
      <Row style={{ marginBottom: "1rem" }}>
        <Form layout="inline">
          <Form.Item label="Select Month" name="month" required>
            <DatePicker
              picker="month"
              format="YYYY-MM"
              onChange={(val) => setMonth(val?.format("YYYY-MM"))}
            />
          </Form.Item>
        </Form>
      </Row>

      {month ? (
        <Row style={{ marginTop: "1rem" }}>
          <Col lg={16} xs={24}>
            <AppUpload
              uploadUrl={uploadGigPayoutsUrl}
              headers={headers}
              onUploaded={onUploaded}
              onUploading={onUploading}
              hasErrorRows={hasErrorRows}
              data={{ month }}
            />
          </Col>
        </Row>
      ) : (
        <Row style={{ marginTop: "1rem" }}>
          <Col>Select a month to upload a file.</Col>
        </Row>
      )}

      {!!Object.keys(processingEarningPayouts).length && !processingEarningPayouts.key && (
        <>
          <Row>
            <Col lg={16} xs={24}>
              <Alert
                type="error"
                message="Errors in sheet data."
                description="Please correct errors and upload again."
                showIcon
                banner
              />
            </Col>
          </Row>
          <Row>
            <Table
              size="small"
              columns={columns}
              dataSource={data}
              rowClassName={addBorderToErrorRows}
              scroll={{ y: 500, x: "100vh" }}
              className="table-with-errors"
            />
          </Row>
        </>
      )}
    </>
  );
};

export default UploadEarningPayoutsPage;
